import React, { Fragment, useEffect, useState } from "react";
import { Col, Row, Input } from "antd";
import "@/styles/components/blogs.scss";
import { useNavigate } from "react-router-dom";
import { getBlogs } from "@/services/common/blogs";
import { getBlogCategories } from "@/services/common/getBlogCategories";
import { SearchOutlined } from "@ant-design/icons";
import BlogCard from "@/components/BlogCard";
import SEOHead from "@/components/SEO/SEOHead";
import { Helmet } from "react-helmet-async";
import slugify from "slugify";

const { Search } = Input;

const Blogs = () => {
  const [blogs, setBlogs] = useState([]); // all one-section blogs
  const [filteredBlogs, setFilteredBlogs] = useState([]); // what we display
  const [categories, setCategories] = useState([]); // category list
  const [selectedCategory, setSelectedCategory] = useState(0); // 0 == "All"

  const navigate = useNavigate();

  // strip HTML helper
  const stripHtmlTags = (htmlContent) => {
    const doc = new DOMParser().parseFromString(htmlContent, "text/html");
    return doc.body.textContent || "";
  };

  // fetch blogs & init
  const fetchBlogs = async () => {
    try {
      const response = await getBlogs();
      setBlogs(response);
      setFilteredBlogs(response);
    } catch (error) {
      console.error(error);
    }
  };

  // fetch categories
  const fetchCategories = async () => {
    try {
      const resp = await getBlogCategories();
      // the API returns an object keyed by index; turn it into an array
      const cats = Object.values(resp)
        .filter((c) => c.isActive)
        .map((c) => ({ id: c.id, name: c.name }));
      setCategories(cats);
    } catch (err) {
      console.error("Error loading categories", err);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  // search by title/content
  const onSearch = (value) => {
    const term = value.toLowerCase();
    const searched = blogs.filter((blog) => {
      return (
        blog.title.toLowerCase().includes(term) ||
        stripHtmlTags(blog.contents[0].sectionContent || "")
          .toLowerCase()
          .includes(term)
      );
    });
    // if a category is active, apply it on top of search
    const result = selectedCategory
      ? searched.filter((b) => b.categoryId === selectedCategory)
      : searched;
    setFilteredBlogs(result);
  };

  // category click handler
  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId);
    // start from the full blogs list
    const base = [...blogs];
    const byCategory = catId ? base.filter((b) => b.categoryId === catId) : base;
    setFilteredBlogs(byCategory);
  };

  // Generate SEO description from blogs
  const generateBlogListDescription = () => {
    if (!filteredBlogs || filteredBlogs.length === 0) {
      return "Explore our collection of informative and engaging blogs covering a wide range of topics.";
    }
    
    const categoryText = selectedCategory !== 0 && categories.length > 0
      ? categories.find(cat => cat.id === selectedCategory)?.name
      : "";
      
    return categoryText
      ? `Explore our ${categoryText} blogs and articles with insightful content and expert perspectives.`
      : "Discover our latest blogs featuring insightful articles, expert advice, and in-depth analysis on various topics.";
  };

  // Extract keywords from all blogs
  const extractBlogListKeywords = () => {
    if (!filteredBlogs || filteredBlogs.length === 0) return "";
    
    // Get keywords from all blogs
    const keywordSets = filteredBlogs
      .slice(0, 5) // Take only first 5 blogs to avoid too many keywords
      .map(blog => blog.keywords || []);
      
    // Flatten and remove duplicates
    const uniqueKeywords = Array.from(new Set(keywordSets.flat()));
    
    // Get category names as keywords
    const categoryNames = categories.map(cat => cat.name);
    
    // Combine all keywords and limit
    return [...uniqueKeywords, ...categoryNames].slice(0, 15).join(", ");
  };

  // Get the base URL from the environment or use a default
  const baseUrl = import.meta.env.VITE_URL || window.location.origin;
  const blogPageUrl = `${baseUrl}/blogs`;
  
  // Get featured image for OG image (first blog's image or a default)
  const featuredImage = 
    filteredBlogs.length > 0 && filteredBlogs[0].coverImages && filteredBlogs[0].coverImages.length > 0
      ? filteredBlogs[0].coverImages[0] 
      : `${baseUrl}/assets/images/blogHeaderIcon.svg`;
      
  // Generate structured data for blog list
  const generateBlogListStructuredData = () => {
    // Get current category name if one is selected
    const currentCategory = selectedCategory !== 0 && categories.length > 0
      ? categories.find(cat => cat.id === selectedCategory)?.name
      : "All Blogs";
    
    const itemListElements = filteredBlogs.slice(0, 10).map((blog, index) => {
      const authorSlug = slugify(blog.authorName || "author", {
        lower: true,
        strict: true,
        replacement: "-",
        remove: /[^\w\s-]/g,
      });
      
      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "BlogPosting",
          "headline": blog.title,
          "description": stripHtmlTags(blog.contents[0]?.sectionContent || "").slice(0, 150),
          "author": {
            "@type": "Person",
            "name": blog.authorName || "Inforens"
          },
          "datePublished": blog.createdAt,
          "dateModified": blog.updatedAt,
          "url": `${baseUrl}/blog/${authorSlug}/${blog.slug}/${blog.id}`,
          "image": blog.coverImages?.[0] || `${baseUrl}/assets/images/blogHeaderIcon.svg`
        }
      };
    });
    
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": itemListElements,
      "name": currentCategory,
      "description": generateBlogListDescription()
    };
  };

  return (
    <Fragment>
      <SEOHead
        title={selectedCategory !== 0 && categories.length > 0
          ? `${categories.find(cat => cat.id === selectedCategory)?.name || 'All'} Blogs | Inforens`
          : "Blogs | Inforens"
        }
        description={generateBlogListDescription()}
        keywords={extractBlogListKeywords()}
        ogTitle={selectedCategory !== 0 && categories.length > 0
          ? `${categories.find(cat => cat.id === selectedCategory)?.name || 'All'} Blogs | Inforens`
          : "Blogs | Inforens"
        }
        ogDescription={generateBlogListDescription()}
        ogImage={featuredImage}
        ogUrl={blogPageUrl}
        ogType="website"
        twitterCard="summary_large_image"
      />
      {filteredBlogs.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(generateBlogListStructuredData())}
          </script>
        </Helmet>
      )}
      <div className="blogs-container">
        <div className="offering">
          <div className="icon-wrapper">
            <img src="/assets/images/blogHeaderIcon.svg" alt="" />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2>We write about important and cool things!</h2>
            <Input
              placeholder="Search blogs..."
              style={{ width: 250 }}
              prefix={<SearchOutlined style={{ color: "#aaa" }} />}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>

          {/* ─────── Categories Filter ─────── */}
          <div className="d-flex flex-wrap services">
            <div className="chip-container d-flex flex-wrap">
              {/* "All" chip */}
              <div
                className={`chip ${selectedCategory === 0 ? "chip-selected" : ""}`}
                onClick={() => handleCategoryClick(0)}
              >
                All
              </div>

              {/* one chip per category */}
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`chip ${selectedCategory === cat.id ? "chip-selected" : ""}`}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  {cat.name}
                </div>
              ))}
            </div>

            <div className="view-services" onClick={() => navigate("/plans")}>
              {/* existing "view-services" button */}
            </div>
          </div>
        </div>

        {/* ─────── Blog Grid ─────── */}
        <Row gutter={[22, 22]}>
          {filteredBlogs.map((data) => (
            <Col key={data.id} xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
              <BlogCard
                date={new Date(data.updatedAt).toLocaleDateString()}
                readTime={data.readingTime}
                title={data.title}
                content={stripHtmlTags(data.contents[0].sectionContent ?? "")}
                coverImage={
                  data.coverImages[0] ||
                  `https://placehold.co/600x400?text=${encodeURIComponent(data.title)}`
                }
                blogId={data.id}
                author={data.authorName}
                slug={data.slug}
              />
            </Col>
          ))}
        </Row>
      </div>
    </Fragment>
  );
};

export default Blogs;
